import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getCerts} from "../../service/dataService";
import {getUserCerts} from "../../service/userService";
import BasicGrid from "../grid/BasicGrid";
import {Button, Divider, Stack, toaster} from "rsuite";
import SuccessNotification from "../notifications/SuccessNotification";
import {
    CERT_ADD_ERROR,
    CERT_ADD_SUCCESS, CERT_EDIT_ERROR, CERT_EDIT_SUCCESS,
} from "../../service/constants";
import ErrorNotification from "../notifications/ErrorNotification";
import {parseISO} from "rsuite/cjs/utils/dateUtils";
import AddCertModal from "../modals/certifications/AddCertModal";
import EditCertModal from "../modals/certifications/EditCertModal";

const defaultColumns = [
    {
        key: 'certName',
        label: 'Certification Name',
        flexGrow: 2,
    },
    {
        key: 'dateEarned',
        label: 'Date Earned',
    },
];

const Certifications = () => {

    // state
    const userInfo = useSelector((state) => state.user.userInfo );
    const [loading, setLoading] = useState(false);
    const [certs, setCerts] = useState([]);
    const [userCerts, setUserCerts] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedCert, setSelectedCert] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    const loadUserCerts = useCallback(() => {
        setLoading(true);
        getUserCerts(userInfo.id).then((certs) => {
            certs.forEach((cert) => {
                cert.dateEarned = parseISO(cert.dateEarned);
            });
            certs.sort((a, b) => (a.dateEarned < b.dateEarned) ? 1 : -1);
            setUserCerts(certs);
            setLoading(false);
        });
    }, [userInfo.id]);

    const handleAddClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={CERT_ADD_SUCCESS} />, { placement: 'topCenter' });
            loadUserCerts();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={CERT_ADD_ERROR} />, { placement: 'topCenter' });
        }
        setAddModalOpen(false);
    },[loadUserCerts]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={CERT_EDIT_SUCCESS} />, { placement: 'topCenter' });
            loadUserCerts();
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={CERT_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [loadUserCerts]);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedCert = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedCert(rowData);
    },[]);

    useEffect(() => {
        setLoading(true);
        getCerts().then((data) => {
                // format the data into InputPicker format for now
                setCerts(data.map(ele => ({label: ele.certName, value: ele.certName})));
            }
        );
        loadUserCerts();
    }, [loadUserCerts]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <h2>Certifications</h2>
                <Stack spacing={2} direction='column' alignItems='flex-start' style={{paddingBottom: '0.5rem'}}>
                    <p>
                        Click a row to edit/delete.
                    </p>
                    <p>
                        Date earned can be edited. If your cert name is incorrect, please delete and reenter the cert.
                    </p>
                </Stack>
                <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Certification</Button>
                <Divider />
                <BasicGrid
                    loading={loading}
                    columns={columns}
                    data={userCerts}
                    handleClick={setClickedCert}
                    exportTitle={`${userInfo.lastName}-${userInfo.firstName}-certifications`}
                />
                <AddCertModal
                    open={addModalOpen}
                    handleClose={handleAddClose}
                    certs={certs}
                    userId={userInfo.id}
                />
                <EditCertModal
                    open={editModalOpen}
                    handleClose={handleEditClose}
                    cert={selectedCert}
                    certs={certs}
                    userId={userInfo.id}
                />
            </div>
        );
    }, [
        addModalOpen,
        certs,
        columns,
        editModalOpen,
        handleAddClose,
        handleEditClose,
        loading,
        selectedCert,
        setClickedCert,
        userCerts,
        userInfo.id,
        userInfo.firstName,
        userInfo.lastName,
    ]);
}

export default Certifications;